import { User } from '@auth0/auth0-react';
import {
  createContext,
  useContext,
  useState,
  PropsWithChildren,
  useMemo,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';

type UserAuth = User | undefined;

export interface AuthState {
  isAuthenticated: boolean;
  user: UserAuth;
  ready: boolean;
  isDemo: boolean;
  token: string | undefined;
}

interface AuthContextType extends AuthState {
  updateAuthState: (newState: AuthState) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }

  return context;
};

export let authStateRef: AuthState = {
  isAuthenticated: false,
  user: undefined,
  ready: false,
  isDemo: false,
  token: undefined,
};

const syncAuthStateRef = (state: AuthState) => {
  authStateRef = state;
};

export const getAuthState = () => authStateRef;

export const AuthProvider = (props: PropsWithChildren) => {
  const { children } = props;
  const [params] = useSearchParams();
  const isDemoInParams = params.get('demo');

  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: undefined,
    ready: false,
    isDemo: isDemoInParams === 'true' ? true : false,
    token: undefined,
  });

  const updateAuthState = useCallback((newState: AuthState) => {
    setAuthState(newState);
    syncAuthStateRef(newState);
  }, []);

  const value = useMemo(() => ({ ...authState, updateAuthState }), [authState, updateAuthState]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

let getTokenFn: (() => Promise<string>) | null = null;

export const setTokenGetter = (fn: () => Promise<string>) => {
  getTokenFn = fn;
};

export const getAccessToken = async () => {
  if (!getTokenFn) {
    throw new Error('Token getter not initialized');
  }
  return getTokenFn();
};
